import { Briefcase, Mic, Newspaper, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { GtmBrain } from "../api/GtmApi";
import {
  parseVoiceChannel,
  parseVoiceTone,
  serializeVoiceChannel,
  serializeVoiceTone,
  type VoiceChannelFields,
  type VoiceDocId,
  type VoiceToneFields,
} from "./voiceProfile";

const DOC_META: Record<
  VoiceDocId,
  { title: string; subtitle: string; icon: "tone" | "linkedin" | "blog" }
> = {
  tone: {
    title: "Tone of voice",
    subtitle: "How you sound, across everything I draft for you.",
    icon: "tone",
  },
  linkedin: {
    title: "LinkedIn posts",
    subtitle: "How LinkedIn should read.",
    icon: "linkedin",
  },
  blog: {
    title: "Blog articles",
    subtitle: "How long-form should read.",
    icon: "blog",
  },
};

const TONE_FIELDS: Array<{
  id: keyof VoiceToneFields;
  label: string;
  kind: "text" | "textarea";
  rows?: number;
  placeholder?: string;
  hint?: string;
}> = [
  {
    id: "archetype",
    label: "Archetype",
    kind: "text",
    placeholder: "e.g. Trusted operator, Sharp peer",
  },
  {
    id: "description",
    label: "Description",
    kind: "textarea",
    rows: 4,
    placeholder: "How you sound in a sentence or two.",
  },
  {
    id: "guidelines",
    label: "Guidelines",
    kind: "textarea",
    rows: 4,
    placeholder: "One guideline per line",
    hint: "One per line",
  },
  {
    id: "preferredLanguage",
    label: "Preferred language",
    kind: "textarea",
    rows: 3,
    placeholder: "Words and phrases to lean on.",
  },
  {
    id: "avoidedLanguage",
    label: "Avoided language",
    kind: "textarea",
    rows: 3,
    placeholder: "Words and phrases to avoid.",
  },
  {
    id: "bannedClaims",
    label: "Banned claims",
    kind: "textarea",
    rows: 3,
    placeholder: "Claims you never make.",
  },
];

const CHANNEL_FIELDS: Array<{
  id: keyof VoiceChannelFields;
  label: string;
  rows: number;
  placeholder?: string;
}> = [
  {
    id: "guidance",
    label: "Guidance",
    rows: 4,
    placeholder: "How this channel should read.",
  },
  {
    id: "examples",
    label: "Examples",
    rows: 5,
    placeholder: "Sample lines or posts that sound right.",
  },
];

type Props = {
  brain: GtmBrain;
  docId: VoiceDocId;
  onBrainChange: (next: GtmBrain) => void;
  onBackToVoice: () => void;
  onBackToOverview: () => void;
};

export default function BrainVoiceDocView({
  brain,
  docId,
  onBrainChange,
  onBackToVoice,
  onBackToOverview,
}: Props) {
  const meta = DOC_META[docId];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [toneDraft, setToneDraft] = useState<VoiceToneFields>(() =>
    parseVoiceTone(brain.toneOfVoice)
  );
  const [channelDraft, setChannelDraft] = useState<VoiceChannelFields>(() =>
    parseVoiceChannel(docId === "linkedin" ? brain.voiceLinkedin : brain.voiceBlog)
  );

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    setToneDraft(parseVoiceTone(brain.toneOfVoice));
    setChannelDraft(
      parseVoiceChannel(docId === "linkedin" ? brain.voiceLinkedin : brain.voiceBlog)
    );
    // Reset local drafts when switching docs or when brain is replaced from server.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, brain.id, brain.sourceFetchId]);

  const scrollToSection = (id: string) => {
    const el = scrollRef.current?.querySelector(`[data-section-id="${id}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const updateTone = (field: keyof VoiceToneFields, value: string) => {
    const next = { ...toneDraft, [field]: value };
    setToneDraft(next);
    onBrainChange({ ...brain, toneOfVoice: serializeVoiceTone(next) });
  };

  const updateChannel = (field: keyof VoiceChannelFields, value: string) => {
    const next = { ...channelDraft, [field]: value };
    setChannelDraft(next);
    const serialized = serializeVoiceChannel(next);
    if (docId === "linkedin") {
      onBrainChange({ ...brain, voiceLinkedin: serialized });
    } else {
      onBrainChange({ ...brain, voiceBlog: serialized });
    }
  };

  const Icon = meta.icon === "linkedin" ? Briefcase : meta.icon === "blog" ? Newspaper : Mic;
  const navChips =
    docId === "tone"
      ? TONE_FIELDS.map((f) => ({ id: f.id, label: f.label }))
      : CHANNEL_FIELDS.map((f) => ({ id: f.id, label: f.label }));

  return (
    <div className="flex h-full w-[min(100%,28rem)] shrink-0 min-w-0 flex-col border-r border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-5 py-3 text-xs text-slate-500">
        <div className="min-w-0 truncate">
          <button
            type="button"
            className="hover:text-slate-800 hover:underline"
            onClick={onBackToOverview}
          >
            Brain
          </button>
          <span className="mx-1.5 text-slate-300">›</span>
          <button
            type="button"
            className="hover:text-slate-800 hover:underline"
            onClick={onBackToVoice}
          >
            Voice
          </button>
          <span className="mx-1.5 text-slate-300">›</span>
          <span className="font-medium text-slate-800">{meta.title}</span>
        </div>
        <button
          type="button"
          onClick={onBackToVoice}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50">
          <Icon className="h-4 w-4 text-teal-600" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900">{meta.title}</h2>
        <p className="mt-1 text-sm text-slate-500">{meta.subtitle}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {navChips.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollToSection(s.id)}
              className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-white hover:text-slate-900"
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-6">
          {docId === "tone"
            ? TONE_FIELDS.map((f) => (
                <section key={f.id} data-section-id={f.id} className="scroll-mt-4">
                  <label className="block text-sm font-semibold text-slate-900">{f.label}</label>
                  {f.kind === "text" ? (
                    <input
                      type="text"
                      value={toneDraft[f.id]}
                      onChange={(e) => updateTone(f.id, e.target.value)}
                      placeholder={f.placeholder}
                      className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
                    />
                  ) : (
                    <textarea
                      value={toneDraft[f.id]}
                      onChange={(e) => updateTone(f.id, e.target.value)}
                      placeholder={f.placeholder}
                      rows={f.rows || 3}
                      className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
                    />
                  )}
                  {f.hint ? (
                    <p className="mt-1 text-[11px] text-slate-400">{f.hint}</p>
                  ) : null}
                </section>
              ))
            : CHANNEL_FIELDS.map((f) => (
                <section key={f.id} data-section-id={f.id} className="scroll-mt-4">
                  <label className="block text-sm font-semibold text-slate-900">{f.label}</label>
                  <textarea
                    value={channelDraft[f.id]}
                    onChange={(e) => updateChannel(f.id, e.target.value)}
                    placeholder={f.placeholder}
                    rows={f.rows}
                    className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
                  />
                </section>
              ))}
        </div>
      </div>
    </div>
  );
}
